import React, { useEffect, useState } from 'react';
import { getInvoices, updateInvoice, downloadInvoicePDF } from '../api/client';
import { usePermissions } from '../contexts/PermissionContext';
import { Download, CheckCircle, Clock, XCircle } from 'lucide-react';

const statusIcon = { paid: CheckCircle, unpaid: Clock, partial: Clock };
const statusColor = { paid: '#10B981', unpaid: '#F59E0B', partial: '#3B82F6' };

export default function Invoices() {
  const { hasPermission } = usePermissions();
  const canEdit        = hasPermission('invoices:edit');
  const canDownloadPDF = hasPermission('invoices:pdf:download');

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getInvoices().then(r => setInvoices(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleMarkPaid = async (id) => {
    await updateInvoice(id, { payment_status: 'paid' });
    load();
  };

  const handleDownload = async (invoice) => {
    const res = await downloadInvoicePDF(invoice.id);
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoice-${invoice.invoice_number}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const totalRevenue = invoices.filter(i => i.payment_status === 'paid').reduce((sum, i) => sum + i.paid_amount, 0);
  const outstanding = invoices.filter(i => i.payment_status !== 'paid').reduce((sum, i) => sum + i.total_amount, 0);

  const showActionsCol = canEdit || canDownloadPDF;

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 20 }}>Invoices</h1>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #F3F4F6' }}>
          <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>Total Invoices</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{invoices.length}</p>
        </div>
        <div style={{ background: '#F0FDF4', borderRadius: 10, padding: 16, border: '1px solid #D1FAE5' }}>
          <p style={{ fontSize: 12, color: '#065F46', marginBottom: 4 }}>Collected Revenue</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: '#059669' }}>${totalRevenue.toLocaleString('en', { minimumFractionDigits: 2 })}</p>
        </div>
        <div style={{ background: '#FEF3C7', borderRadius: 10, padding: 16, border: '1px solid #FDE68A' }}>
          <p style={{ fontSize: 12, color: '#92400E', marginBottom: 4 }}>Outstanding</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: '#D97706' }}>${outstanding.toLocaleString('en', { minimumFractionDigits: 2 })}</p>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Invoice #', 'Customer', 'Subtotal', 'Tax', 'Total', 'Status', 'Due Date', ...(showActionsCol ? ['Actions'] : [])].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={showActionsCol ? 8 : 7} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={showActionsCol ? 8 : 7} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>No invoices yet</td></tr>
            ) : invoices.map(inv => {
              const Icon = statusIcon[inv.payment_status] || Clock;
              const color = statusColor[inv.payment_status] || '#9CA3AF';
              return (
                <tr key={inv.id} style={{ borderBottom: '1px solid #F9FAFB' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#2563EB' }}>{inv.invoice_number}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{inv.customer_name}</div>
                    <div style={{ color: '#9CA3AF', fontSize: 11 }}>{inv.customer_email}</div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>${inv.subtotal.toFixed(2)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>${inv.tax_amount.toFixed(2)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: '#111827' }}>${inv.total_amount.toFixed(2)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: color + '18', color, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>
                      <Icon size={12} />{inv.payment_status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#6B7280' }}>
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                  </td>
                  {showActionsCol && (
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {canDownloadPDF && (
                          <button onClick={() => handleDownload(inv)}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#F3F4F6', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            <Download size={13} /> PDF
                          </button>
                        )}
                        {canEdit && inv.payment_status !== 'paid' && (
                          <button onClick={() => handleMarkPaid(inv.id)}
                            style={{ padding: '5px 10px', background: '#D1FAE5', color: '#065F46', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
